import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronDown, House, LayoutGrid, Settings } from "lucide-react";
import { useRequestCounts } from "@/modules/requests";
import { useCan } from "@/shared/lib/session";
import { hrefParts } from "./apps";
import { pageOf, useAreas, type Area, type AreaKey, type AreaPage } from "./areas";

/** The menu's sections: the user's own and the trade-specific apps, the work areas, then reports. */
const SECTIONS: AreaKey[][] = [["me", "requests", "pos", "appointments", "realestate"], ["sales", "buying", "stock", "money", "team", "work"], ["reports"]];

/** Where the user is: the area and page of the current location. */
export function useHere() {
  const location = useRouterState({ select: (s) => s.location });
  const areas = useAreas();
  const here = pageOf(areas, location.pathname, location.search as Record<string, unknown>);
  return { areas, area: here?.area, page: here?.page, path: location.pathname };
}


function PageLink({ page, current, onNavigate }: { page: AreaPage; current: boolean; onNavigate?: () => void }) {
  const { to, search } = hrefParts(page.to);
  return (
    <Link to={to} search={search as never} onClick={onNavigate} aria-current={current ? "page" : undefined}
      className={clsx("block rounded-md px-2 py-[5px]", current ? "font-semibold text-ledger-dark" : "text-ink-2 hover:text-ink")}>
      {page.label}
    </Link>
  );
}

/** An area's pages under it: the main ones, then the rest behind "more" (open when the current page is one of them), then its settings. */
function AreaPages({ area, current, onNavigate }: { area: Area; current?: AreaPage; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const label = t(`areas.${area.key}`);
  const main = area.pages.filter((p) => !p.more);
  const rest = area.pages.filter((p) => p.more);
  const [opened, setOpened] = useState(false);
  const more = opened || !!current?.more;
  return (
    <nav aria-label={label} className="ms-[21px] mt-1 mb-2 space-y-px border-s-[1.5px] border-line ps-3 text-[14px]">
      {main.map((p) => <PageLink key={p.to} page={p} current={p === current} onNavigate={onNavigate} />)}
      {rest.length > 0 && (
        <>
          <button type="button" onClick={() => setOpened(!opened)} aria-expanded={more}
            className="flex w-full items-center gap-1 rounded-md px-2 py-[5px] text-start text-ink-3 hover:text-ink">
            {t("nav.otherPages")}
            <ChevronDown className={clsx("size-3.5 transition-transform", more && "rotate-180")} aria-hidden />
          </button>
          {more && rest.map((p) => <PageLink key={p.to} page={p} current={p === current} onNavigate={onNavigate} />)}
        </>
      )}
    </nav>
  );
}

const row = (active: boolean, collapsed?: boolean) => clsx(
  "relative flex w-full items-center gap-3 rounded-[10px] px-3 py-[7px] text-start",
  collapsed && "justify-center px-0 py-2",
  active ? "bg-surface font-semibold text-ink shadow-[0_1px_2px_rgba(27,36,48,.08),0_0_0_1px_rgba(27,36,48,.05)]" : "text-ink-2 hover:bg-ink/[.04] hover:text-ink",
);

/**
 * The menu: home, then the areas the user may open. On a wide screen an area opens on its
 * first page and lists its pages under it; in the phone's drawer (`drawer`) tapping an area
 * only shows its pages, so the next tap goes straight where the user meant. `collapsed`
 * leaves an icon rail.
 */
export function Sidebar({ collapsed, drawer, onNavigate }: { collapsed?: boolean; drawer?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const { areas, area: here, page, path } = useHere();
  const counts = useRequestCounts();
  const [opened, setOpened] = useState<AreaKey | undefined>(here?.key);
  const icon = (a: Area, active: boolean) => <a.icon className={clsx("size-[18px] shrink-0", active ? "text-ledger" : "text-ink-3")} aria-hidden />;
  const badge = (a: Area) => (a.key === "requests" ? counts.data?.waiting : undefined);

  const item = (a: Area) => {
    const label = t(`areas.${a.key}`);
    const active = a.key === here?.key;
    const n = badge(a);
    const mark = !!n && (collapsed
      ? <span className="absolute end-1.5 top-1 size-2 rounded-full bg-saffron" />
      : <span className="num rounded-full bg-saffron px-1.5 text-[11px] leading-[18px] font-semibold text-white">{n}</span>);
    const many = a.pages.length > 1 && !a.flat;
    // In the drawer an area with pages unfolds instead of leaving.
    if (drawer && many) {
      const open = opened === a.key;
      return (
        <li key={a.key}>
          <button type="button" className={row(active)} aria-expanded={open} onClick={() => setOpened(open ? undefined : a.key)}>
            {icon(a, active)}<span className="flex-1">{label}</span>{mark}
            <ChevronDown className={clsx("size-4 text-ink-3 transition-transform", open && "rotate-180")} aria-hidden />
          </button>
          {open && <AreaPages area={a} current={active ? page : undefined} onNavigate={onNavigate} />}
        </li>
      );
    }
    const first = a.pages.find((p) => !p.more) ?? a.pages[0];
    const { to, search } = hrefParts(first.to);
    return (
      <li key={a.key}>
        <Link to={to} search={search as never} onClick={onNavigate} className={row(active, collapsed)}
          title={collapsed ? label : undefined} aria-label={collapsed ? label : undefined} aria-current={active && !many ? "page" : undefined}>
          {icon(a, active)}{!collapsed && <span className="flex-1">{label}</span>}{mark}
        </Link>
        {active && many && !collapsed && <AreaPages area={a} current={page} onNavigate={onNavigate} />}
      </li>
    );
  };

  const sections = SECTIONS.map((keys) => areas.filter((a) => keys.includes(a.key))).filter((s) => s.length > 0);
  // Whoever may switch apps on finds the ones that are off a click away.
  const appsLink = can("core.apps.manage");
  const appsActive = appsLink && path === "/settings/apps";
  const settingsActive = (path === "/settings" || path.startsWith("/settings/")) && !appsActive;
  return (
    <nav className="no-scrollbar flex h-full flex-col overflow-y-auto px-3 pt-2 pb-4 text-[14.5px]" aria-label="Main">
      <ul className="space-y-0.5">
        <li>
          <Link to="/" onClick={onNavigate} className={row(path === "/", collapsed)} aria-current={path === "/" ? "page" : undefined}
            title={collapsed ? t("nav.home") : undefined} aria-label={collapsed ? t("nav.home") : undefined}>
            <House className={clsx("size-[18px] shrink-0", path === "/" ? "text-ledger" : "text-ink-3")} aria-hidden />
            {!collapsed && <span className="flex-1">{t("nav.home")}</span>}
          </Link>
        </li>
        {sections.map((s, i) => (
          <li key={s[0].key}>
            {/* The user's own pages follow home directly; each other section starts with a rule. */}
            {(i > 0 || !SECTIONS[0].includes(s[0].key)) && <div className="mx-3 my-3 border-t border-line" aria-hidden />}
            <ul className="space-y-0.5">{s.map(item)}</ul>
          </li>
        ))}
      </ul>
      <div className="mt-auto space-y-0.5 pt-4">
        {appsLink && (
          <Link to="/settings/apps" onClick={onNavigate} className={row(appsActive, collapsed)} aria-current={appsActive ? "page" : undefined}
            title={collapsed ? t("nav.allApps") : undefined} aria-label={collapsed ? t("nav.allApps") : undefined}>
            <LayoutGrid className={clsx("size-[18px] shrink-0", appsActive ? "text-ledger" : "text-ink-3")} aria-hidden />
            {!collapsed && <span className="flex-1">{t("nav.allApps")}</span>}
          </Link>
        )}
        <Link to="/settings" onClick={onNavigate} className={row(settingsActive, collapsed)} aria-current={settingsActive ? "page" : undefined}
          title={collapsed ? t("nav.settings") : undefined} aria-label={collapsed ? t("nav.settings") : undefined}>
          <Settings className={clsx("size-[18px] shrink-0", settingsActive ? "text-ledger" : "text-ink-3")} aria-hidden />
          {!collapsed && <span className="flex-1">{t("nav.settings")}</span>}
        </Link>
      </div>
    </nav>
  );
}
